// Field metadata + pure helpers for the Update Security popup.
// All fields are editable regardless of their current value.
// Types/max-lengths mirror the backend SecMaster DTO / DB column definitions.

export const FIELD_TYPES = {
  TEXT: "text",
  NUMBER: "number",
  DATE: "date",
  SELECT: "select",
};

const YES_NO_TRUE = new Set(["y", "yes", "1", "true"]);
const YES_NO_FALSE = new Set(["n", "no", "0", "false"]);

// Normalise "Y"/"N"/"Yes"/"No"/"1"/"0"/"true"/"false" to the canonical "Y"/"N"
// sent to and received from the backend. Returns "" for unrecognised values so
// the Select shows its empty placeholder rather than a broken selection.
export const normalizeYesNoValue = (raw) => {
  const s = String(raw ?? "").trim().toLowerCase();
  if (YES_NO_TRUE.has(s)) return "Y";
  if (YES_NO_FALSE.has(s)) return "N";
  return "";
};

// Order within this array is the order fields appear within their group
// (editable group first, then disabled group). identifier + price + poolFactor
// are pinned to the very front of the popup (see PINNED_KEYS); the remaining
// fields follow the product's logical grouping — Basic Information, then
// Currency & Coupon, Dates, Security Characteristics, Ratings & Classification.
// The group headers are NOT rendered; they only define the field sequence.
export const SECURITY_FIELDS = [
  // Pinned (always shown first, in this order)
  { key: "identifier",             label: "Identifier",              type: FIELD_TYPES.TEXT,    maxLength: 50 },
  { key: "price",                  label: "Price",                   type: FIELD_TYPES.NUMBER,  alwaysEditable: true, max: 150, maxDecimals: 4 },
  { key: "poolFactor",             label: "Factor",                  type: FIELD_TYPES.NUMBER,  alwaysEditable: true, max: 1, maxDecimals: 12 },

  // Basic Information
  { key: "securityDesc",           label: "Description",             type: FIELD_TYPES.TEXT,    maxLength: 512 },
  { key: "issuer",                 label: "Issuer",                  type: FIELD_TYPES.SELECT,  optionsKey: "issuer",                   maxLength: 50 },
  { key: "bondType",               label: "Type",                    type: FIELD_TYPES.SELECT,  optionsKey: "bondType",                 maxLength: 50 },
  { key: "subType",                label: "Sub Type",                type: FIELD_TYPES.SELECT,  optionsKey: "subType",                  maxLength: 256 },
  { key: "sector",                 label: "Sector",                  type: FIELD_TYPES.SELECT,  optionsKey: "sector",                   maxLength: 50 },

  // Currency & Coupon Information
  { key: "currency",               label: "Currency",                type: FIELD_TYPES.SELECT,  optionsKey: "currency",                 maxLength: 50 },
  { key: "issueCurrency",          label: "Issue Currency",          type: FIELD_TYPES.SELECT,  optionsKey: "issueCurrency",            maxLength: 50 },
  { key: "couponPer",              label: "Coupon %",                type: FIELD_TYPES.NUMBER,  max: 100, maxDecimals: 12 },
  { key: "couponType",             label: "Coupon Type",             type: FIELD_TYPES.SELECT,  optionsKey: "couponType",               maxLength: 50 },
  { key: "interestPaymentFrequency", label: "Payment Frequency",     type: FIELD_TYPES.SELECT,  optionsKey: "interestPaymentFrequency", maxLength: 50 },
  { key: "conventionMethod",       label: "Convention Method",       type: FIELD_TYPES.SELECT,  optionsKey: "conventionMethod",         maxLength: 50 },

  // Dates
  { key: "issueDate",              label: "Issue Date",              type: FIELD_TYPES.DATE },
  { key: "firstCouponDate",        label: "First Coupon Date",       type: FIELD_TYPES.DATE },
  { key: "lastCouponDate",         label: "Last Coupon Date",        type: FIELD_TYPES.DATE },
  { key: "nextCallDate",           label: "Next Call Date",          type: FIELD_TYPES.DATE },
  { key: "callStartDate",          label: "Call Start Date",         type: FIELD_TYPES.DATE },
  { key: "maturityDate",           label: "Maturity Date",           type: FIELD_TYPES.DATE },

  // Security Characteristics
  { key: "isCallable",             label: "Callable",                type: FIELD_TYPES.SELECT,  optionsKey: "isCallable",               maxLength: 50, yesNo: true },
  { key: "isPerpetual",            label: "Perpetual",               type: FIELD_TYPES.SELECT,  optionsKey: "isPerpetual",              maxLength: 50, yesNo: true },
  { key: "isZeroCoupon",           label: "Zero Coupon",             type: FIELD_TYPES.SELECT,  optionsKey: "isZeroCoupon",             maxLength: 50, yesNo: true },
  { key: "isInsured",              label: "Insured",                 type: FIELD_TYPES.SELECT,  optionsKey: "isInsured",                maxLength: 50, yesNo: true },
  { key: "isExemptFromFedTax",     label: "Fed Tax Exempt",          type: FIELD_TYPES.SELECT,  optionsKey: "isExemptFromFedTax",       maxLength: 50, yesNo: true },

  // Ratings & Classification
  { key: "govState",               label: "State",                   type: FIELD_TYPES.SELECT,  optionsKey: "govState",                 maxLength: 50 },
  { key: "incomeSource",           label: "Income Source",           type: FIELD_TYPES.SELECT,  optionsKey: "incomeSource",             maxLength: 256 },
  { key: "securityCharge",         label: "Factored Securities",     type: FIELD_TYPES.SELECT,  optionsKey: "securityCharge",           maxLength: 255 },
  { key: "spRating",               label: "S&P",                     type: FIELD_TYPES.SELECT,  optionsKey: "spRating",                 maxLength: 50 },
  { key: "moodyRating",            label: "Moody's",                 type: FIELD_TYPES.SELECT,  optionsKey: "moodyRating",              maxLength: 50 },
  { key: "underlyingIssuerRating", label: "Underlying Issuer Rating", type: FIELD_TYPES.TEXT,   maxLength: 50 },
];

// A value is "missing" only when null/undefined or blank string.
export const hasValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

export const isFieldEditable = (field) => field.key !== "identifier";

// Convert a stored date (ISO / yyyy-MM-dd / etc.) to the yyyy-MM-dd string a
// DatePicker's dayjs value is formatted from. Returns "" when it cannot be parsed.
export const toDateInputValue = (value) => {
  if (!hasValue(value)) return "";
  const str = String(value).trim();
  const ymd = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) return `${ymd[1]}-${ymd[2]}-${ymd[3]}`;
  const normalized = /[zZ]|[+-]\d{2}:?\d{2}$/.test(str) ? str : `${str}Z`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return "";
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  return `${date.getUTCFullYear()}-${mm}-${dd}`;
};

// MM/DD/YYYY for read-only display of date fields.
export const formatDateMMDDYYYY = (value) => {
  const iso = toDateInputValue(value);
  if (!iso) return String(value ?? "");
  const [yyyy, mm, dd] = iso.split("-");
  return `${mm}/${dd}/${yyyy}`;
};

// String to seed a controlled input for an editable field.
// yesNo fields are normalised to "Y"/"N" so the MUI Select matches its options.
export const toInputValue = (field, rawValue) => {
  if (!hasValue(rawValue)) return "";
  if (field.yesNo) return normalizeYesNoValue(rawValue);
  if (field.type === FIELD_TYPES.DATE) return toDateInputValue(rawValue);
  return String(rawValue);
};

// Read-only display string for a disabled field.
// yesNo fields show "Yes"/"No" regardless of how the backend stored the value.
export const formatDisplayValue = (field, rawValue) => {
  if (!hasValue(rawValue)) return "";
  if (field.yesNo) {
    const n = normalizeYesNoValue(rawValue);
    return n === "Y" ? "Yes" : n === "N" ? "No" : String(rawValue);
  }
  if (field.type === FIELD_TYPES.DATE) return formatDateMMDDYYYY(rawValue);
  return String(rawValue);
};

// Returns an error message string, or "" when the value is valid.
// Empty values are allowed — the spec marks no field as required.
export const validateField = (field, value) => {
  const str = typeof value === "string" ? value.trim() : value;
  if (!hasValue(str)) return "";

  if (field.type === FIELD_TYPES.NUMBER) {
    const num = Number(str);
    if (!Number.isFinite(num)) return "Enter a valid number";

    const effectiveMin = field.min !== undefined ? field.min : 0;
    const hasMax = field.max !== undefined;

    if (num < effectiveMin) {
      return hasMax
        ? `Value must be between ${effectiveMin} and ${field.max}`
        : "Value cannot be negative";
    }
    if (hasMax && num > field.max) {
      return `Value must be between ${effectiveMin} and ${field.max}`;
    }
    if (field.maxDecimals !== undefined) {
      const decimals = String(str).split(".")[1];
      if (decimals && decimals.length > field.maxDecimals) {
        return `Max ${field.maxDecimals} decimal places allowed`;
      }
    }
    return "";
  }

  if (field.type === FIELD_TYPES.DATE) {
    return toDateInputValue(str) ? "" : "Enter a valid date";
  }

  if (field.type === FIELD_TYPES.SELECT) return "";

  // Text
  if (field.maxLength && String(str).length > field.maxLength) {
    return `Max ${field.maxLength} characters`;
  }
  return "";
};

// Build the PUT body containing ONLY the fields the user actually changed from
// the record's original value (the modify endpoint expects a sparse payload).
// A field that was cleared is sent as null; numbers are cast to Number.
export const buildModifiedPayload = (securityData = {}, formValues = {}, editableFields = []) => {
  const payload = {};

  editableFields.forEach((field) => {
    const originalInput = toInputValue(field, securityData[field.key]);
    let current = formValues[field.key];
    if (typeof current === "string") current = current.trim();
    const currentInput = current === null || current === undefined ? "" : String(current);

    if (currentInput === originalInput) return; // unchanged → skip

    if (!hasValue(currentInput)) {
      payload[field.key] = null; // cleared
    } else if (field.type === FIELD_TYPES.NUMBER) {
      payload[field.key] = Number(currentInput);
    } else {
      payload[field.key] = currentInput;
    }
  });

  return payload;
};
